import { FC, ReactElement, useState } from 'react';
import InfoSmallIcon from '@/components/Icons/InfoSmallIcon';
import InfoBubble from '@/components/Icons/InfoBubble';

type MetricsEntryProps = {
  data: string | undefined;
  label: string;
  unit?: string;
  tooltip?: string;
};

const MetricsEntry: FC<MetricsEntryProps> = ({ data, label, unit = '', tooltip }): ReactElement => {
  const [hovered, setHovered] = useState(false);
  const formatNumber = (raw: string): string => {
    const numericValue = Number(raw);
    if (Number.isNaN(numericValue)) return raw;
    // Keep one decimal if present, otherwise show integer with locale formatting
    const hasDecimals = !Number.isInteger(numericValue);
    return numericValue.toLocaleString(undefined, {
      minimumFractionDigits: hasDecimals ? 1 : 0,
      maximumFractionDigits: hasDecimals ? 1 : 0
    });
  };
  return (
    <>
      {data && (
        <div
          className={`
            relative flex flex-col items-start justify-center gap-2 p-5 w-full h-full
            flex-[1_0_0] rounded-xs bg-white elevated p-20
          `}
        >
          <h1 className="text-h1 text-secondary-navy-500">{`${formatNumber(data)}${unit}`}</h1>
          <div
            className={`
              text-sm text-neutral-500 mt-8 flex items-center gap-6 whitespace-nowrap
            `}
          >
            <span>{label}</span>
            {tooltip ? (
              <span
                className="relative inline-flex items-center cursor-pointer"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
              >
                <InfoSmallIcon
                  className={hovered ? 'text-primary-500' : 'text-neutral-300'}
                  aria-label="Info"
                />
                <span
                  className={`
                    absolute bottom-18 left-1/2 -translate-x-1/2 z-20 w-105 h-64
                    transform-gpu transition-all duration-150 ease-out
                    ${hovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
                  `}
                >
                  <InfoBubble className="relative z-0" />
                  <span
                    className={`
                      absolute z-10 top-0 left-0 p-12 pr-8
                      text-neutral-500 text-body-xs whitespace-normal
                    `}
                  >
                    {tooltip}
                  </span>
                </span>
              </span>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
};

export default MetricsEntry;
